import { Box, Card, Divider, Stack, TextField, ClickAwayListener, Button } from '@mui/material';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useFlowSyncTransport } from '@/state/mutations/flows/use-flow-sync-transport';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@/hooks/use-notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { calculateFlowDurationFromSlots } from './calculate';

type InlineEndLabelFormProps = {
  flow: FlowDto;
  onClose: () => void;
  onCancel: () => void;
};

export default function InlineEndLabelForm({ flow, onClose, onCancel }: InlineEndLabelFormProps) {
  const { t } = useTranslation();
  const notification = useNotifications();
  const setFlow = useFlowStore((s) => s.setFlow);
  const { emitFlowSync, canEmit } = useFlowSyncTransport();
  const [endLabelValue, setEndLabelValue] = useState(flow?.endOfFlowLabel || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [initialLabel] = useState(flow?.endOfFlowLabel || '');

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const flowStartOffset = flow?.slot?.startOffsetMinutes ?? 0;
  // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
  const fixedDuration = flow?.durationPlannedMinutes;
  const calculatedDuration = calculateFlowDurationFromSlots(flow?.slots ?? []);
  const flowDuration =
    fixedDuration !== undefined && fixedDuration > 0
      ? fixedDuration
      : calculatedDuration !== undefined
        ? calculatedDuration
        : 0;
  const flowEndOffset = flowStartOffset + flowDuration;
  const endTime = minuteOffsetToTime(flowEndOffset);

  const handleSubmit = useCallback(async () => {
    if (!flow || !canEmit) {
      notification.notifyError(t('flow.notification.updateError'));
      return;
    }

    // Validate max length
    if (endLabelValue.length > 100) {
      notification.notifyWarning('Label must be 100 characters or less');
      return;
    }

    setIsSubmitting(true);

    try {
      // Optimistically update the flow store
      const updatedFlow: FlowDto = {
        ...flow,
        endOfFlowLabel: endLabelValue.trim() || undefined,
        version: (flow.version ?? 0) + 1,
      };
      setFlow(updatedFlow);

      // Sync with server
      const payload = {
        flow: {
          id: flow.id,
          version: flow.version,
          title: flow.title,
          description: flow.description,
          endOfFlowLabel: endLabelValue.trim() || undefined,
        },
      };

      void (async () => {
        const { ok } = await emitFlowSync(payload);
        setIsSubmitting(false);
        if (ok) {
          notification.notifySuccess(
            t('flow.notification.updateSuccess', { title: flow.title ?? t('myEventsList.unTitled') }),
          );
          onClose();
        } else {
          setFlow(flow);
          notification.notifyError(t('flow.notification.updateError'));
        }
      })();
    } catch {
      setIsSubmitting(false);
      setFlow(flow);
      notification.notifyError(t('flow.notification.updateError'));
    }
  }, [flow, canEmit, emitFlowSync, endLabelValue, setFlow, notification, t, onClose]);

  const handleCancel = useCallback(() => {
    setEndLabelValue(flow?.endOfFlowLabel || '');
    onCancel();
  }, [flow, onCancel]);

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      // Don't close if clicking on the form itself
      const target = event.target as HTMLElement;
      if (target.closest('[data-end-label-form]')) {
        return;
      }
      handleCancel();
    },
    [handleCancel],
  );

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [handleCancel]);

  const isValid = endLabelValue.length <= 100;
  const isDirty = endLabelValue.trim() !== initialLabel.trim();

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Card
        data-end-label-form
        elevation={0}
        sx={{
          cursor: 'default',
          bgcolor: 'slot.break.selected',
          outlineWidth: '2px',
          outlineColor: 'secondary.main',
          outlineStyle: 'solid',
          overflow: 'hidden',
          borderRadius: 0,
          height: '32px',
          minHeight: '32px',
          maxHeight: '32px',
        }}
      >
        <Stack direction="row" alignItems="stretch" height="100%">
          {/* Left timing section */}
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              minWidth: '75px',
              flexShrink: 0,
              height: '100%',
              minHeight: '32px',
              cursor: 'default',
            }}
          >
            <Stack direction="column" alignItems="center" justifyContent="center" spacing={0}>
              <Box
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontFamily: 'Roboto',
                  lineHeight: 1.0,
                  letterSpacing: '0.033em',
                }}
              >
                {endTime}
              </Box>
            </Stack>
          </Stack>

          <Divider orientation="vertical" flexItem />

          {/* Middle section with text input */}
          <Stack direction="row" alignItems="center" sx={{ p: 1, flex: 1, minWidth: 0 }}>
            <TextField
              inputRef={inputRef}
              variant="standard"
              value={endLabelValue}
              onChange={(e) => setEndLabelValue(e.target.value)}
              placeholder={t('flows.detail.slotList.detail.endLabel')}
              inputProps={{ maxLength: 100 }}
              disabled={isSubmitting}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isValid) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              sx={{
                'flex': 1,
                'minWidth': 0,
                '& .MuiInputBase-root': {
                  'fontSize': '1rem',
                  'fontWeight': 500,
                  'lineHeight': 1.57,
                  'fontFamily': 'Roboto',
                  '&::before': { display: 'none' },
                  '&::after': { display: 'none' },
                  '&:hover::before': { display: 'none' },
                  '&:hover::after': { display: 'none' },
                  '&.Mui-focused::before': { display: 'none' },
                  '&.Mui-focused::after': { display: 'none' },
                },
                '& .MuiInputBase-input': {
                  py: 0,
                  pl: 0,
                  pr: 1,
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'text.primary',
                },
              }}
            />
          </Stack>

          {/* Right side: Cancel and Approve buttons (match slot timing form) */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            sx={{
              minWidth: '75px',
              width: '120px',
              flexShrink: 0,
              p: 1,
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleCancel}
              disabled={isSubmitting}
              sx={{
                'flex': 1,
                'minWidth': 30,
                'color': 'action.active',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <CancelOutlinedIcon fontSize="small" />
            </Button>
            <Button
              variant={isValid && isDirty && !isSubmitting ? 'contained' : 'outlined'}
              color={isValid && isDirty && !isSubmitting ? 'success' : 'secondary'}
              size="small"
              onClick={handleSubmit}
              disabled={isSubmitting || !isValid || !isDirty}
              sx={{
                'flex': 1,
                'minWidth': 30,
                'color': isValid && isDirty && !isSubmitting ? undefined : 'secondary.main',
                '&:hover': {
                  backgroundColor: isValid && isDirty && !isSubmitting ? undefined : 'action.hover',
                },
                '&.Mui-disabled': {
                  color: 'action.disabled',
                },
              }}
            >
              <CheckCircleIcon fontSize="small" />
            </Button>
          </Stack>
        </Stack>
      </Card>
    </ClickAwayListener>
  );
}
